import { CompanyContactPerson } from 'shared/types/contact';

/**
 * Ruwe contactpersoon zoals hij als **relatie** meekomt op een andere entity —
 * bv. `supplierAssignment.contact` of `document.contactPerson`. Dat is de
 * `Contact`-entity, niet het compacte formaat van
 * `GET /contact/company/:id/contacts`.
 */
export interface RawContactRelation {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  jobTitle?: string | null;
  phone_mobile?: string | null;
  phone_direct?: string | null;
  company?: { id: number; name?: string | null } | null;
}

/**
 * Eenvoudige e-mailcontrole, **identiek** aan `isValidEmailAddress` in de backend
 * (`shared/utils/email.util.ts`): genoeg om te weten of er naar dit adres gemaild
 * kan worden. Geen volledige RFC-validatie — die hoort op de server, niet in een
 * weergavehelper. Wijzigt de backend-regel, pas deze dan mee aan, anders belooft
 * de UI iets anders dan wat er gebeurt.
 */
const heeftGeldigAdres = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

/**
 * Zet een contactpersoon-relatie om naar het formaat dat `CompanyContactPicker`
 * gebruikt.
 *
 * Waarom dit nodig is: de kiezer werkt met het compacte `CompanyContactPerson`
 * (met `name` en `hasEmail` al berekend), maar op een ticket of document komt de
 * contactpersoon als volledige entity mee. Zonder deze omzetting zou elke
 * gebruiker van de kiezer zijn eigen naamopbouw en adrescontrole schrijven, en
 * dan verschilt de weergave per scherm.
 */
export const toCompanyContactPerson = (
  raw: RawContactRelation | null | undefined,
  fallbackCompany?: { id: number; name?: string | null } | null,
): CompanyContactPerson | null => {
  if (!raw?.id) return null;

  const email = raw.email?.trim() ?? '';
  const naam = `${raw.firstName ?? ''} ${raw.lastName ?? ''}`.trim();
  const company = raw.company ?? fallbackCompany ?? null;

  return {
    id: raw.id,
    firstName: raw.firstName ?? '',
    lastName: raw.lastName ?? '',
    name: naam || email || `Contact #${raw.id}`,
    email,
    hasEmail: heeftGeldigAdres(email),
    jobTitle: raw.jobTitle?.trim() || null,
    phone: raw.phone_mobile?.trim() || raw.phone_direct?.trim() || null,
    companyId: company?.id ?? null,
    companyName: company?.name ?? null,
  };
};

/**
 * Het adres waarnaar een mail voor deze leverancier vertrekt: dat van de gekozen
 * contactpersoon, en anders het algemene adres van de firma. Spiegelt
 * `resolveSupplierAssignmentEmail` in de backend, zodat de UI hetzelfde toont als
 * wat er effectief gebeurt.
 */
export const resolveSupplierRecipientEmail = (
  contact: { email?: string | null } | null | undefined,
  companyEmail: string | null | undefined,
): string | null => {
  const contactEmail = contact?.email?.trim();
  if (contactEmail && heeftGeldigAdres(contactEmail)) return contactEmail;

  const firmaEmail = companyEmail?.trim();
  if (firmaEmail && heeftGeldigAdres(firmaEmail)) return firmaEmail;

  return null;
};
